import {
  formatInZone,
  hoursApart,
  isUnsociableHour,
  specialtyLabel,
  type Specialty,
} from "@so/shared";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAvailability, useBookAppointment, useClinician, useMe } from "../api/queries.js";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  PageHeader,
  Textarea,
  cx,
} from "../components/ui.js";

interface DayGroup {
  readonly key: string;
  readonly label: string;
  readonly slots: readonly { starts_at: string; ends_at: string }[];
}

/**
 * Groups open windows by the patient's own calendar day.
 *
 * Grouping by UTC day, or by the specialist's day, produces a list where
 * "Tuesday" contains times that are Monday evening for the person reading it.
 */
function groupByViewerDay(
  slots: readonly { starts_at: string; ends_at: string }[],
  timezone: string,
): DayGroup[] {
  const groups = new Map<string, DayGroup>();

  for (const slot of slots) {
    const { date } = formatInZone(slot.starts_at, timezone);

    const existing = groups.get(date);
    if (existing) {
      groups.set(date, { ...existing, slots: [...existing.slots, slot] });
    } else {
      groups.set(date, { key: date, label: date, slots: [slot] });
    }
  }

  return [...groups.values()];
}

export function SpecialistDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const { data: me } = useMe();
  const { data: clinician, isPending: loadingClinician, error: clinicianError } = useClinician(id);
  const { data: availability, isPending: loadingSlots, error: slotsError } = useAvailability(id);

  const [selected, setSelected] = useState<string | undefined>();
  const [reason, setReason] = useState("");
  const book = useBookAppointment();

  const viewerTimezone = me?.timezone ?? "UTC";

  const days = useMemo(
    () => groupByViewerDay(availability?.slots ?? [], viewerTimezone),
    [availability, viewerTimezone],
  );

  if (loadingClinician || loadingSlots) return <Loading label="Loading specialist" />;
  if (clinicianError) return <ErrorNote error={clinicianError} />;
  if (slotsError) return <ErrorNote error={slotsError} />;
  if (!clinician) return <ErrorNote error={new Error("Specialist not found.")} />;

  const gap = selected
    ? Math.abs(hoursApart(selected, viewerTimezone, clinician.timezone))
    : 0;

  const awkwardForSpecialist =
    selected !== undefined && isUnsociableHour(selected, clinician.timezone);

  const reasonTooShort = reason.trim().length < 10;

  async function submit() {
    if (selected === undefined || reasonTooShort) return;

    const appointment = await book.mutateAsync({
      clinician_id: id,
      starts_at: selected,
      reason: reason.trim(),
    });

    navigate(`/consultations/${appointment.appointment_id}`);
  }

  return (
    <>
      <PageHeader eyebrow="Book a consultation" title={clinician.name}>
        {clinician.bio}
      </PageHeader>

      <div className="mb-7 flex flex-wrap items-center gap-2">
        <Badge tone="accent">{specialtyLabel(clinician.specialty as Specialty)}</Badge>
        <Badge>{clinician.credentials}</Badge>
        <Badge>{clinician.hospital}</Badge>
        <Badge>{clinician.years_experience} years</Badge>
      </div>

      {days.length === 0 ? (
        <EmptyState
          title="No open times at the moment"
          action={
            <Link to="/specialists">
              <Button variant="secondary">Browse other specialists</Button>
            </Link>
          }
        >
          This specialist has no windows open right now. Our coordination team can often arrange a
          time directly — get in touch and we will look.
        </EmptyState>
      ) : (
        <div className="grid gap-7 lg:grid-cols-[1.3fr_1fr]">
          <section>
            <h2 className="mb-1 text-[17px] font-semibold">Choose a time</h2>
            <p className="text-ink-soft mb-4 text-[14px]">
              Shown in your timezone
              <span className="font-mono"> ({viewerTimezone})</span>.
            </p>

            <div className="space-y-5">
              {days.map((day) => (
                <div key={day.key}>
                  <h3 className="text-ink-soft mb-2 text-[13.5px] font-medium">{day.label}</h3>
                  <div className="flex flex-wrap gap-2">
                    {day.slots.map((slot) => {
                      const local = formatInZone(slot.starts_at, viewerTimezone);
                      const isSelected = selected === slot.starts_at;

                      return (
                        <button
                          key={slot.starts_at}
                          type="button"
                          onClick={() => setSelected(slot.starts_at)}
                          aria-pressed={isSelected}
                          className={cx(
                            "rounded-md border px-3.5 py-2 font-mono text-[14px] transition-colors",
                            isSelected
                              ? "border-accent bg-accent text-white"
                              : "border-line-strong bg-surface hover:bg-surface-2",
                          )}
                        >
                          {local.time}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <Card className="h-fit p-5">
            <h2 className="mb-4 text-[17px] font-semibold">Your request</h2>

            {selected === undefined ? (
              <p className="text-ink-faint text-[14.5px]">
                Pick a time to continue.
              </p>
            ) : (
              <div className="space-y-4">
                <div className="bg-surface-2 rounded-lg px-4 py-3">
                  <p className="text-[15px] font-medium">
                    {formatInZone(selected, viewerTimezone).date}
                  </p>
                  <p className="mt-0.5 font-mono text-[14px]">
                    {formatInZone(selected, viewerTimezone).time}{" "}
                    {formatInZone(selected, viewerTimezone).zoneAbbreviation}
                  </p>
                  {gap >= 1 && (
                    <p className="text-ink-faint mt-2 text-[13px] leading-relaxed">
                      That is {formatInZone(selected, clinician.timezone).time} for{" "}
                      {clinician.name.split(" ")[0]} in{" "}
                      <span className="font-mono">{clinician.timezone}</span> —{" "}
                      {gap % 1 === 0 ? gap : gap.toFixed(1)} hours apart.
                    </p>
                  )}
                </div>

                {/* The specialist published this window, so it is not an error -
                    but a patient choosing between two equivalent slots may well
                    prefer the one that is not the middle of someone's night. */}
                {awkwardForSpecialist && (
                  <Alert tone="caution">
                    This falls outside normal working hours where the specialist is. They have
                    offered it, so it is available — another time may simply suit you both better.
                  </Alert>
                )}

                <Field
                  label="What would you like a second opinion on?"
                  required
                  hint="A short summary is enough. You can upload your records after booking."
                  error={
                    reason.length > 0 && reasonTooShort
                      ? "Please write at least a sentence."
                      : undefined
                  }
                >
                  <Textarea
                    rows={5}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={2000}
                    placeholder="I was diagnosed with… and my doctor has recommended… I would like a view on whether that is the right approach."
                  />
                </Field>

                {book.error !== null && <ErrorNote error={book.error} />}

                <Button
                  full
                  loading={book.isPending}
                  disabled={reasonTooShort}
                  onClick={() => void submit()}
                >
                  Request this consultation
                </Button>

                <p className="text-ink-faint text-[12.5px] leading-relaxed">
                  The specialist will confirm before it is final. You will be emailed either way.
                </p>
              </div>
            )}
          </Card>
        </div>
      )}
    </>
  );
}
